import { useEffect, useRef, useState } from 'react';

const MIN_COLUMN_WIDTH = 60;

interface ResizeState {
    columnId: string;
    startX: number;
    startWidthPx: number;
}

export function useColumnWidths() {
    const [widths, setWidths] = useState<Record<string, number>>({});
    const resizeRef = useRef<ResizeState | null>(null);

    useEffect(() => {
        const handleMove = (event: PointerEvent) => {
            const state = resizeRef.current;
            if (!state) return;
            const delta = event.clientX - state.startX;
            const next = Math.max(MIN_COLUMN_WIDTH, state.startWidthPx + delta);
            setWidths((prev) => ({ ...prev, [state.columnId]: next }));
        };

        const handleUp = () => {
            if (!resizeRef.current) return;
            resizeRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            if (resizeRef.current) {
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                resizeRef.current = null;
            }
        };
    }, []);

    const startResize = (
        columnId: string,
        startWidthPx: number,
        event: React.PointerEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        resizeRef.current = { columnId, startX: event.clientX, startWidthPx };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    return { widths, startResize };
}
